import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../styles/theme';
import { describeComparable } from '../marketComparablesMap';
import { useMarketTone } from '../marketTone';
import { MARKET_FONT, MARKET_TEAL_LIGHT, marketSmallSx } from './MarketParts';

const rowSx = (t, selected, hovered) => ({
  display: 'flex',
  gap: 1.25,
  p: 1,
  borderRadius: '10px',
  border: `1px solid ${selected ? colors.primary : t.rowBorder}`,
  bgcolor: hovered || selected ? t.rowActive : t.card,
  cursor: 'pointer',
  flexShrink: 0,
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
  transition: 'background-color 0.12s ease, border-color 0.12s ease',
});
const thumbSx = (t) => ({
  position: 'relative',
  width: 72,
  height: 60,
  flexShrink: 0,
  borderRadius: '8px',
  overflow: 'hidden',
  bgcolor: t.thumbBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: t.thumbIcon,
  border: 0,
  p: 0,
});
const badgeSx = {
  position: 'absolute', right: 4, bottom: 4, px: 0.5, borderRadius: '4px',
  bgcolor: 'rgba(0,0,0,0.65)', color: '#FFF', fontSize: '10px', fontFamily: MARKET_FONT, lineHeight: '14px',
};
const priceSx = (t) => ({ fontFamily: MARKET_FONT, fontSize: '14px', fontWeight: 700, color: t.big, lineHeight: 1.3 });
const lineSx = (t) => ({ ...marketSmallSx, color: t.legendRight });
const matchChipSx = (t, strong, filled = false) => ({
  fontFamily: MARKET_FONT,
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '18px',
  px: 1,
  borderRadius: '999px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  color: strong ? t.chipStrong : t.chipWeak,
  border: `1px solid ${strong ? t.chipStrongBorder : t.chipWeakBorder}`,
  bgcolor: filled ? t.chipFilled : 'transparent',
});

function Thumb({ comparable, photos, onOpenPhotos, tone }) {
  const Icon = comparable.terrioType === 'casa' || comparable.terrioType === 'terreno' ? HomeOutlinedIcon : ApartmentOutlinedIcon;
  const [broken, setBroken] = useState(false);
  const photo = !broken ? photos[0] : null;
  const clickable = Boolean(photo && onOpenPhotos);
  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={clickable ? (e) => { e.stopPropagation(); onOpenPhotos(comparable, 0); } : undefined}
      aria-label={clickable ? 'Ver fotos' : undefined}
      sx={{ ...thumbSx(tone), cursor: clickable ? 'zoom-in' : 'inherit' }}
      data-testid="market-comparable-thumb"
    >
      {photo ? (
        <Box
          component="img"
          src={photo}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : <Icon sx={{ fontSize: 24 }} aria-hidden />}
      {photo && photos.length > 1 ? <Box component="span" sx={badgeSx}>{`1/${photos.length}`}</Box> : null}
    </Box>
  );
}

/**
 * Lista de comparables (pestaña Comparables del informe y vista Comparables del Dock). Orden
 * = el de la API (muy parecidas primero); no se reordena. Hover / selección por
 * `comparables[].key` para resaltar el chip del mapa. Sin link, fuente ni inmobiliaria.
 */
export default function MarketComparablesList({
  block,
  hoveredKey = null,
  selectedKey = null,
  onHoverKey = undefined,
  onSelectKey = undefined,
  onOpenPhotos = undefined,
  centerChip = false,
}) {
  const rowsRef = useRef(new Map());
  useEffect(() => {
    if (!selectedKey) return;
    const el = rowsRef.current.get(selectedKey);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedKey]);

  const list = block?.comparables || [];
  const tone = useMarketTone();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="market-comparables-list">
      {list.map((c) => {
        const d = describeComparable(c, block);
        const chip = d.chipLabel ? (
          <Box
            component="span"
            sx={{ ...matchChipSx(tone, d.verySimilar || c.match === 'similar', d.verySimilar), ...(centerChip ? { alignSelf: 'center' } : {}) }}
            data-testid="market-comparable-match"
            data-placement={centerChip ? 'center' : 'inline'}
          >
            {d.chipLabel}
          </Box>
        ) : null;
        const selected = selectedKey === c.key;
        const hovered = hoveredKey === c.key;
        return (
          <Box
            key={c.key}
            ref={(el) => { if (el) rowsRef.current.set(c.key, el); else rowsRef.current.delete(c.key); }}
            sx={rowSx(tone, selected, hovered)}
            onMouseEnter={onHoverKey ? () => onHoverKey(c.key) : undefined}
            onMouseLeave={onHoverKey ? () => onHoverKey(null) : undefined}
            onClick={onSelectKey ? () => onSelectKey(selected ? null : c.key) : undefined}
            data-testid="market-comparable-row"
            data-key={c.key}
            data-selected={selected ? 'true' : undefined}
            data-hovered={hovered ? 'true' : undefined}
          >
            <Thumb comparable={c} photos={d.photos} onOpenPhotos={onOpenPhotos} tone={tone} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={priceSx(tone)} noWrap data-testid="market-comparable-title">{d.title || '—'}</Typography>
                {!centerChip ? chip : null}
              </Box>
              {d.sizeTotal ? <Typography sx={{ ...lineSx(tone), color: tone.text }} noWrap>{d.sizeTotal}</Typography> : null}
              {d.attrs ? <Typography sx={lineSx(tone)} noWrap>{d.attrs}</Typography> : null}
              <Typography sx={{ ...marketSmallSx, color: tone.muted }} noWrap data-testid="market-comparable-meta">{d.meta}</Typography>
            </Box>
            {/* Modal: el chip va en su propia columna, centrado en el alto de la card. */}
            {centerChip ? chip : null}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Carrusel de fotos dentro del mismo modal (sin abrir otro diálogo). Cubre el cuerpo; ‹ › y
 * flechas del teclado; Escape o ✕ cierran solo el carrusel.
 */
const photoCloseSx = {
  position: 'absolute', top: 8, right: 8, zIndex: 1, width: 40, height: 40,
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: '#FFFFFF' },
  '&.Mui-focusVisible': { outline: `2px solid ${MARKET_TEAL_LIGHT}`, outlineOffset: 1 },
};

export function MarketPhotoViewer({ photos, index = 0, onIndexChange, onClose }) {
  const n = photos.length;
  const i = ((index % n) + n) % n;
  const go = (delta) => onIndexChange(((i + delta) % n + n) % n);
  return (
    <Box
      role="dialog"
      aria-label="Fotos del comparable"
      tabIndex={-1}
      ref={(el) => el?.focus?.()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
        if (e.key === 'ArrowLeft' && n > 1) go(-1);
        if (e.key === 'ArrowRight' && n > 1) go(1);
      }}
      sx={{
        position: 'absolute', inset: 0, zIndex: 2, bgcolor: 'rgba(18,18,18,0.96)', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, outline: 'none',
      }}
      data-testid="market-photo-viewer"
    >
      <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {n > 1 ? (
          <IconButton onClick={() => go(-1)} aria-label="Foto anterior" sx={{ position: 'absolute', left: 0, color: '#FFF', bgcolor: 'rgba(0,0,0,0.4)' }}>
            <ChevronLeftIcon />
          </IconButton>
        ) : null}
        <Box
          component="img"
          src={photos[i]}
          alt=""
          referrerPolicy="no-referrer"
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px' }}
          data-testid="market-photo-current"
        />
        {n > 1 ? (
          <IconButton onClick={() => go(1)} aria-label="Foto siguiente" sx={{ position: 'absolute', right: 0, color: '#FFF', bgcolor: 'rgba(0,0,0,0.4)' }}>
            <ChevronRightIcon />
          </IconButton>
        ) : null}
      </Box>
      <Typography sx={{ ...marketSmallSx, mt: 1 }} data-testid="market-photo-count">{`${i + 1} / ${n}`}</Typography>
      {/* Después del área de la foto y con zIndex: antes esa área (relative, desde y = 16 px)
          quedaba encima de la × (top 8, 40 px) y solo sus 8 px de arriba recibían el clic. */}
      <IconButton onClick={onClose} aria-label="Cerrar fotos" sx={photoCloseSx} data-testid="market-photo-close">
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Box>
  );
}
